import { useEffect, useRef } from "react";
import Lenis from "lenis";

/**
 * Site-wide smooth scrolling via Lenis (https://lenis.darkroom.engineering).
 *
 * Mounted once in `App.tsx`. Lenis hijacks the wheel/touch event
 * stream, runs scroll updates on a single rAF cycle, and applies a
 * gentle exponential ease so scrolls glide instead of stepping.
 * Returns a ref to the Lenis instance so route-change handlers
 * (e.g. `<ScrollToTop>`) can call `.scrollTo(0, { immediate: true })`
 * to jump without animation.
 *
 * Honours `prefers-reduced-motion: reduce` by skipping init entirely
 * — native scroll takes over and the user's OS preference wins.
 *
 * Companion CSS in `main.css`:
 *   html, body { overscroll-behavior: none; }  // kill rubber-band
 *   html.lenis-stopped { overflow: hidden; }   // optional pause hook
 */
export function useSmoothScroll(): React.MutableRefObject<Lenis | null> {
  const ref = useRef<Lenis | null>(null);

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    const lenis = new Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      syncTouch: false,
    });
    ref.current = lenis;

    let rafId = 0;
    function raf(time: number): void {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      ref.current = null;
    };
  }, []);

  return ref;
}
